import React, { Component } from 'react';
import Rest from 'tools/rest';
import { Table, Select, Row, Col, Space, Divider, Tag, message } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import dayjs from 'dayjs';
import Preloader from 'components/preloader';

const { Option } = Select;

function uniqById(list = []) {
  const map = new Map();
  list.forEach((x) => {
    if (x && x.id != null) map.set(x.id, x);
  });
  return Array.from(map.values());
}

class MaterialsWriteoffsReport extends Component {
  state = {
    loading: false,
    period: {
      kind: 'month', // month | quarter | year
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      quarter: Math.floor(new Date().getMonth() / 3) + 1,
    },
    personPeriod: {
      kind: 'month',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      quarter: Math.floor(new Date().getMonth() / 3) + 1,
    },
    personFilters: {
      department_ids: [],
      roles: [],
      user_ids: [],
      material_ids: [],
      category_ids: [],
    },
    dictionaries: {
      departments: [],
      users: [],
      roles: [],
      materials: [],
      categories: [],
    },
    allowed_roles: [],
    detail_rows: [],
    summary_rows: [],
  };

  componentDidMount() {
    this.loadDictionaries();
    this.loadReport();
  }

  componentDidUpdate(prevProps, prevState) {
    const p0 = prevState.period;
    const p1 = this.state.period;
    if (JSON.stringify(p0) !== JSON.stringify(p1)) {
      this.loadReport();
    }

    const pp0 = prevState.personPeriod;
    const pp1 = this.state.personPeriod;
    const f0 = prevState.personFilters;
    const f1 = this.state.personFilters;
    if (JSON.stringify(pp0) !== JSON.stringify(pp1) || JSON.stringify(f0) !== JSON.stringify(f1)) {
      this.loadPersonDetail();
    }
  }

  loadDictionaries = () => {
    // departments, users, materials, categories — минимально для фильтров
    Promise.all([
      Rest.get('/api/v1/departments'),
      Rest.get('/api/v1/users/help_desk_users.json'),
      Rest.get('/api/v1/directory/user_roles'),
      Rest.get('/api/v1/materials'),
      Rest.get('/api/v1/material_categories'),
    ])
      .then(([depRes, usersRes, rolesRes, matRes, catRes]) => {
        const departments = depRes.data?.departments || depRes.data || [];
        const users = usersRes.data?.users || usersRes.data || [];
        const rolesHash = rolesRes.data?.user_roles || rolesRes.data || {};
        const materials = matRes.data?.materials || matRes.data || [];
        const categories = catRes.data?.material_categories || catRes.data || [];

        const roles = Object.keys(rolesHash || {}).map((key) => ({ label: key, value: key }));
        const roleIdToKey = Object.entries(rolesHash || {}).reduce((memo, [k, v]) => {
          memo[String(v)] = k;
          return memo;
        }, {});

        this.setState({
          dictionaries: {
            departments: Array.isArray(departments) ? departments : [],
            users: Array.isArray(users) ? users : [],
            roles,
            roleIdToKey,
            materials: Array.isArray(materials) ? materials : [],
            categories: Array.isArray(categories) ? categories : [],
          },
        });
      })
      .catch((e) => console.error(e));
  };

  loadReport = () => {
    const { period } = this.state;
    this.setState({ loading: true });
    Rest.get('/api/v1/reports/materials_writeoffs', { params: { period, filters: {}, include_detail: false } })
      .then((res) => {
        const { summary_rows, allowed_roles } = res.data || {};
        this.setState({
          summary_rows: Array.isArray(summary_rows) ? summary_rows : [],
          allowed_roles: Array.isArray(allowed_roles) ? allowed_roles : [],
        });
      })
      .catch((e) => {
        console.error(e);
        const errText =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          'Ошибка формирования отчёта';
        message.error(errText);
      })
      .finally(() => this.setState({ loading: false }));
  };

  loadPersonDetail = () => {
    const { personPeriod, personFilters } = this.state;
    const hasAnyFilter =
      (personFilters.user_ids || []).length ||
      (personFilters.department_ids || []).length ||
      (personFilters.roles || []).length ||
      (personFilters.material_ids || []).length ||
      (personFilters.category_ids || []).length;

    if (!hasAnyFilter) {
      this.setState({ detail_rows: [] });
      return;
    }

    Rest.get('/api/v1/reports/materials_writeoffs', {
      params: { period: personPeriod, filters: personFilters, include_detail: true },
    })
      .then((res) => {
        const { detail_rows } = res.data || {};
        this.setState({
          detail_rows: Array.isArray(detail_rows) ? detail_rows : [],
        });
      })
      .catch((e) => {
        console.error(e);
        const errText =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          'Ошибка формирования детализации';
        message.error(errText);
      });
  };

  render() {
    const { loading, period, personPeriod, personFilters, dictionaries, detail_rows, summary_rows, allowed_roles } = this.state;


    const yearOptions = [];
    const y0 = new Date().getFullYear();
    for (let y = y0 - 2; y <= y0 + 1; y += 1) yearOptions.push(y);

    const monthNames = [
      'Январь',
      'Февраль',
      'Март',
      'Апрель',
      'Май',
      'Июнь',
      'Июль',
      'Август',
      'Сентябрь',
      'Октябрь',
      'Ноябрь',
      'Декабрь',
    ];

    const allowedRoleSet = new Set((allowed_roles || []).map(String));

    const REPORT_ROLE_KEYS = [
      'main_engineer',
      'service_engineer',
      'connect_engineer',
      'technical_engineer',
      'video_engineer',
    ];
    const reportRoleSet = new Set(REPORT_ROLE_KEYS);
    
    const departmentNameToRoleKeys = (name) => {
      const s = String(name || '').toLowerCase();
      if (s.includes('administrative_department')) return ['main_engineer'];
      if (s.includes('service_department')) return ['service_engineer'];
      if (s.includes('connection_department')) return ['connect_engineer'];
      if (s.includes('exploitation_department')) return ['technical_engineer', 'video_engineer'];
      return [];
    };

    const selectedDepartments = (dictionaries.departments || []).filter((d) =>
      (personFilters.department_ids || []).includes(d.id),
    );
    const hasDeptFilter = (personFilters.department_ids || []).length > 0;
    const rolesBySelectedDepartments = selectedDepartments
      .flatMap((d) => departmentNameToRoleKeys(d?.name))
      .filter(Boolean);
    const deptRoleSet = new Set(rolesBySelectedDepartments.map(String));

    const baseRoleOptions = (dictionaries.roles || []).filter((r) => {
      const key = String(r.value);
      const byReport = reportRoleSet.has(key);
      const byAllowed = allowedRoleSet.size === 0 ? true : allowedRoleSet.has(key);
      const byDept = hasDeptFilter ? deptRoleSet.has(key) : true;
      return byReport && byAllowed && byDept;
    });
    const roleOptions = baseRoleOptions;

    const roleKeyOfUser = (u) => {
      if (!u) return '';
      if (typeof u.role === 'number') return dictionaries.roleIdToKey?.[String(u.role)] || String(u.role);
      if (u.role != null) return String(u.role);
      return '';
    };

    const filteredUsers = (dictionaries.users || []).filter((u) => {
      const rk = roleKeyOfUser(u);
      const okAllowed = allowedRoleSet.size === 0 ? true : allowedRoleSet.has(rk);
      const okDeptRoles = hasDeptFilter ? deptRoleSet.has(rk) : true;
      const okRoleFilter = (personFilters.roles || []).length ? (personFilters.roles || []).includes(rk) : true;
      const okDeptFilter = (personFilters.department_ids || []).length ? (personFilters.department_ids || []).includes(u?.department_id) : true;
      return okAllowed && okDeptRoles && okRoleFilter && okDeptFilter;
    });
    const userOptions = filteredUsers.map((u) => ({ label: u.name, value: u.id }));

    const departmentOptions = (dictionaries.departments || [])
      .filter((d) => departmentNameToRoleKeys(d?.name).length > 0)
      .map((d) => ({ label: d.name, value: d.id }));
    const materialOptions = (dictionaries.materials || []).map((m) => ({ label: m.name, value: m.id }));
    const categoryOptions = (dictionaries.categories || []).map((c) => ({ label: c.name, value: c.id }));

    const detailColumns = [
      {
        title: 'Дата списания',
        dataIndex: 'writeoff_at',
        key: 'writeoff_at',
        width: 140,
        render: (iso) => {
          if (!iso) return '—';
          if (iso === 'TOTAL') return 'Итого за месяц';
          return dayjs(iso).format('DD.MM.YYYY');
        },
      },
      {
        title: 'Материал',
        key: 'material',
        render: (_, r) => <div>{r.material?.name || r.material_name || '—'}</div>,
      },
      {
        title: 'Кол-во',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 90,
        align: 'right',
      },
      {
        title: 'Сотрудник',
        key: 'user',
        render: (_, r) => (
          <div>
            <div>{r.user?.name || r.user_name || '—'}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {r.user?.department?.name ? <Tag>{r.user.department.name}</Tag> : null}
              {r.user?.role ? <Tag color="blue">{r.user.role}</Tag> : null}
            </div>
          </div>
        ),
      },
    ];

    const summaryColumns = [
      {
        title: 'Материал',
        dataIndex: 'material_name',
        key: 'material_name',
      },
      {
        title: 'Факт (на начало)',
        key: 'fact_opening',
        width: 130,
        align: 'right',
        render: (_, r) => {
          const first = Array.isArray(r.months) ? r.months[0] : null;
          return first ? first.fact : '—';
        },
      },
      {
        title: 'Списано за период',
        dataIndex: 'total_quantity',
        key: 'total_quantity',
        width: 140,
        align: 'right',
      },
      {
        title: 'Приход за период',
        dataIndex: 'income_quantity',
        key: 'income_quantity',
        width: 140,
        align: 'right',
      },
      {
        title: 'Остаток (на конец)',
        key: 'ending_last',
        width: 140,
        align: 'right',
        render: (_, r) => {
          const arr = Array.isArray(r.months) ? r.months : [];
          const last = arr.length ? arr[arr.length - 1] : null;
          return last ? last.ending : '—';
        },
      },
      {
        title: 'Итог',
        dataIndex: 'period_writeoff_total',
        key: 'period_writeoff_total',
        width: 110,
        align: 'right',
      },
    ];

    const detailByMaterial = new Map();
    (detail_rows || []).forEach((r) => {
      const mid = r?.material?.id;
      if (!mid) return;
      if (!detailByMaterial.has(mid)) {
        detailByMaterial.set(mid, {
          key: `m-${mid}`,
          material_id: mid,
          material_name: r.material?.name,
          quantity: 0,
          writeoff_at: 'TOTAL',
          user: null,
          children: [],
        });
      }
      const parent = detailByMaterial.get(mid);
      parent.quantity += Number(r.quantity || 0);
      parent.children.push({ ...r, key: `mv-${r.move_id}` });
    });
    const detailData = Array.from(detailByMaterial.values()).sort((a, b) =>
      String(a.material_name || '').localeCompare(String(b.material_name || ''), 'ru'),
    );
    const summaryData = (summary_rows || []).map((r) => ({ ...r, key: r.material_id }));

    return (
      <Preloader loading={loading}>
        <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <span>Период:</span>
              <Select
                style={{ width: 140 }}
                value={period.kind}
                onChange={(v) => this.setState({ period: { ...period, kind: v } })}
              >
                <Option value="month">Месяц</Option>
                <Option value="quarter">3 месяца</Option>
                <Option value="year">Год</Option>
              </Select>
              <span>Год:</span>
              <Select
                style={{ width: 110 }}
                value={period.year}
                onChange={(v) => this.setState({ period: { ...period, year: v } })}
              >
                {yearOptions.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
              {period.kind === 'month' && (
                <>
                  <span>Месяц:</span>
                  <Select
                    style={{ width: 160 }}
                    value={period.month}
                    onChange={(v) => this.setState({ period: { ...period, month: v } })}
                  >
                    {monthNames.map((name, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </>
              )}
              {period.kind === 'quarter' && (
                <>
                  <span>Стартовый месяц:</span>
                  <Select
                    style={{ width: 160 }}
                    value={period.month}
                    onChange={(v) => this.setState({ period: { ...period, month: v } })}
                  >
                    {monthNames.map((name, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </>
              )}
            </Space>
          </Col>
        </Row>

        <Divider orientation="left">Свод по материалам (сумма за период)</Divider>
        <Table size="small" bordered pagination={{ pageSize: 50, showSizeChanger: true }} columns={summaryColumns} dataSource={summaryData} />

        <Divider orientation="left">Списания по сотруднику (за месяц)</Divider>

        <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
          <Col span={24}>
            <Space wrap>
              <span>Месяц:</span>
              <Select
                style={{ width: 110 }}
                value={personPeriod.year}
                onChange={(v) => this.setState({ personPeriod: { ...personPeriod, year: v } })}
              >
                {yearOptions.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
              <Select
                style={{ width: 180 }}
                value={personPeriod.month}
                onChange={(v) => this.setState({ personPeriod: { ...personPeriod, month: v } })}
              >
                {monthNames.map((name, i) => (
                  <Option key={i + 1} value={i + 1}>
                    {name}
                  </Option>
                ))}
              </Select>
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                style={{ width: 260 }}
                placeholder="Отдел"
                value={personFilters.department_ids}
                options={departmentOptions}
                onChange={(v) => {
                  const nextDepartmentIds = v || [];
                  const selectedDeps = (dictionaries.departments || []).filter((d) => nextDepartmentIds.includes(d.id));
                  const nextDeptRoleSet = new Set(
                    selectedDeps
                      .flatMap((d) => departmentNameToRoleKeys(d?.name))
                      .filter(Boolean)
                      .map(String),
                  );

                  const nextRoles =
                    nextDepartmentIds.length === 0
                      ? (personFilters.roles || [])
                      : (personFilters.roles || []).filter((r) => nextDeptRoleSet.has(String(r)));

                  const nextUserIds = (personFilters.user_ids || []).filter((uid) => {
                    const u = (dictionaries.users || []).find((x) => x.id === uid);
                    if (!u) return false;
                    const rk = roleKeyOfUser(u);
                    const okDept = nextDepartmentIds.length ? nextDepartmentIds.includes(u.department_id) : true;
                    const okRole = nextRoles.length ? nextRoles.includes(rk) : true;
                    const okDeptRole = nextDepartmentIds.length ? nextDeptRoleSet.has(rk) : true;
                    const okAllowed = allowedRoleSet.size === 0 ? true : allowedRoleSet.has(rk);
                    return okDept && okRole && okDeptRole && okAllowed;
                  });

                  this.setState({
                    personFilters: {
                      ...personFilters,
                      department_ids: nextDepartmentIds,
                      roles: nextRoles,
                      user_ids: nextUserIds,
                    },
                  });
                }}
              />
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                style={{ width: 260 }}
                placeholder="Роль"
                value={personFilters.roles}
                options={roleOptions}
                onChange={(v) => {
                  const nextRoles = v || [];
                  // при смене ролей "подчищаем" выбранных пользователей
                  const nextUserIds = (personFilters.user_ids || []).filter((uid) => {
                    const u = (dictionaries.users || []).find((x) => x.id === uid);
                    if (!u) return false;
                    const rk = roleKeyOfUser(u);
                    if (!rk) return false;
                    return nextRoles.length ? nextRoles.includes(rk) : true;
                  });
                  this.setState({
                    personFilters: { ...personFilters, roles: nextRoles, user_ids: nextUserIds },
                  });
                }}
              />
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                style={{ width: 320 }}
                placeholder="Сотрудник"
                value={personFilters.user_ids}
                options={userOptions}
                onChange={(v) => this.setState({ personFilters: { ...personFilters, user_ids: v || [] } })}
              />
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                style={{ width: 360 }}
                placeholder="Материал"
                value={personFilters.material_ids}
                options={materialOptions}
                onChange={(v) => this.setState({ personFilters: { ...personFilters, material_ids: v || [] } })}
              />
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                style={{ width: 300 }}
                placeholder="Категория"
                value={personFilters.category_ids}
                options={categoryOptions}
                onChange={(v) => this.setState({ personFilters: { ...personFilters, category_ids: v || [] } })}
              />
            </Space>
          </Col>
        </Row>

        <Table
          size="small"
          bordered
          columns={detailColumns}
          dataSource={detailData}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          expandable={{ defaultExpandAllRows: true, expandRowByClick: true }}
          scroll={{ x: 1100 }}
        />
      </Preloader>
    );
  }
}

export default MaterialsWriteoffsReport;

